import { makeAutoObservable } from 'mobx'
import { isEmail, isLink } from 'shared/lib'
import { getEmailDomain, getUrlDomain } from 'shared/lib/getDomains'

const EMAIL_DOMAIN_MISMATCH_WARNING =
  "It looks like your business email domain doesn't match your business website domain"

export class ContactInfoStore {
  private _firstName = ''
  private _lastName = ''
  private _businessTitle = ''
  private _businessEmail = ''
  private _businessEmailError = ''
  private _websiteUrl = ''
  private _websiteUrlError = ''
  private _consent = false

  constructor() {
    makeAutoObservable(this)
  }

  get firstName() {
    return this._firstName
  }

  get lastName() {
    return this._lastName
  }

  get businessTitle() {
    return this._businessTitle
  }

  get businessEmail() {
    return this._businessEmail
  }

  get businessEmailError() {
    return this._businessEmailError
  }

  get websiteUrl() {
    return this._websiteUrl
  }

  get websiteUrlError() {
    return this._websiteUrlError
  }

  get consent() {
    return this._consent
  }

  private get _isFirstNameValid() {
    return this._firstName.trim().length > 0
  }

  private get _isLastNameValid() {
    return this._lastName.trim().length > 0
  }

  private get _isBusinessTitleValid() {
    return this._businessTitle.trim().length > 0
  }

  private get _isBusinessEmailValid() {
    return isEmail(this._businessEmail)
  }

  get isWebsiteUrlValid() {
    return isLink(this._websiteUrl)
  }

  private get _websiteUrlDomain() {
    try {
      return getUrlDomain(new URL(this._websiteUrl))
    } catch {
      return undefined
    }
  }

  get businessEmailDomainWarning() {
    if (!this._isBusinessEmailValid || !this.isWebsiteUrlValid) return ''

    const emailDomain = getEmailDomain(this._businessEmail)
    const urlDomain = this._websiteUrlDomain
    if (!emailDomain || !urlDomain) return ''

    return emailDomain === urlDomain ? '' : EMAIL_DOMAIN_MISMATCH_WARNING
  }

  get isNextDisabled() {
    return !(
      this._isFirstNameValid &&
      this._isLastNameValid &&
      this._isBusinessTitleValid &&
      this._isBusinessEmailValid &&
      this.isWebsiteUrlValid &&
      this._consent
    )
  }

  setFirstName = (value: string) => {
    this._firstName = value
  }

  setLastName = (value: string) => {
    this._lastName = value
  }

  setBusinessTitle = (value: string) => {
    this._businessTitle = value
  }

  setBusinessEmail = (value: string) => {
    this._businessEmail = value
    this._businessEmailError = ''
  }

  setWebsiteUrl = (value: string) => {
    this._websiteUrl = value
    this._websiteUrlError = ''
  }

  toggleConsent = () => {
    this._consent = !this._consent
  }

  validateBusinessEmail = () => {
    this._businessEmailError = this._isBusinessEmailValid ? '' : 'Enter a valid email'
  }

  validateWebsiteUrl = () => {
    this._websiteUrlError = this.isWebsiteUrlValid ? '' : 'Enter a valid URL'
  }

  reset = () => {
    this._firstName = ''
    this._lastName = ''
    this._businessTitle = ''
    this._businessEmail = ''
    this._businessEmailError = ''
    this._websiteUrl = ''
    this._websiteUrlError = ''
    this._consent = false
  }
}
